import React from 'react';
import { C } from '../constants';
import PedidoFiscal from './PedidoFiscal';
import {
  estadoSiigo, catalogosSiigo, listarDocumentosSiigo, consultarDocumentoSiigo,
  pdfDocumentoSiigo, reenviarDocumentoSiigo, estamparDocumentoSiigo, erroresDianSiigo,
  verColaPagos, procesarColaPagos, listarMediosPago,
} from '../services/pagos';

/**
 * FACTURACIÓN DIAN (Siigo) — la misma cuenta Alebas que factura FluxAI.
 *
 * Esta pantalla existe porque el modo de falla de facturar es silencioso: la FV se rechaza
 * EN Siigo (centro de costo faltante, tipo de documento inactivo, cliente sin municipio) y
 * desde acá se veía como «no pasó nada». Acá se ve qué resolvió el catálogo, qué herramienta
 * está disponible y por qué la DIAN rechazó un documento.
 *
 * ⚠️ Una FV ya estampada NO se anula: se corrige con nota crédito. Por eso no hay botón de
 * anular en esta pantalla — se llama desde el pedido, que es donde está el contexto.
 */

const CAJA = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' };
const TITULO = { fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 8px' };

function Semaforo({ ok, label, detalle }) {
  const col = ok ? C.teal : C.amber;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: col, flex: '0 0 auto' }} />
      <span style={{ fontSize: 12, color: C.heading, fontWeight: 600 }}>{label}</span>
      {detalle && <span style={{ fontSize: 11, color: C.muted }}>{detalle}</span>}
    </div>
  );
}

const HERRAMIENTAS = [
  ['factura_dian', 'Factura electrónica (FV)'],
  ['nota_credito', 'Nota crédito (NC)'],
  ['nota_debito', 'Nota débito (ND)'],
  ['recibo', 'Comprobante de ingreso'],
  ['recibo_caja', 'Recibo de caja (RC)'],
  ['remision', 'Remisión'],
  ['documento_soporte', 'Documento soporte (DS) — giro al técnico'],
  ['centro_costo', 'Centro de costo Solar Hub'],
  ['iva', 'IVA 19%'],
  ['productos', 'Alta de productos'],
];

/**
 * LA COLA AUTOMÁTICA. Todo pasa solo: el pago aprobado encola factura → recibo de caja →
 * asiento; la obra cerrada encola el giro al técnico. Esta tabla existe porque automatizar
 * sin mirar es peor que no automatizar: si Siigo se cayó justo cuando entró la plata, la
 * venta queda cobrada y sin facturar. Lo que se rindió tiene que verse, no vivir en un log.
 */
function ColaAutomatica() {
  const [filas, setFilas] = React.useState([]);
  const [msg, setMsg] = React.useState('');
  const [todos, setTodos] = React.useState(false);

  const cargar = React.useCallback(async (incluirHechos) => {
    const r = await verColaPagos(incluirHechos).catch((e) => ({ ok: false, error: e.message }));
    if (r?.ok) { setFilas(r.trabajos || []); setMsg(''); } else { setMsg(r?.error || 'No se pudo leer la cola.'); }
  }, []);

  React.useEffect(() => { cargar(todos); }, [cargar, todos]);

  const empujar = async () => {
    setMsg('Procesando…');
    const r = await procesarColaPagos().catch((e) => ({ ok: false, error: e.message }));
    setMsg(r?.ok ? `${r.hechos} hechos · ${r.fallidos} con fallo` : (r?.error || 'No se pudo procesar.'));
    cargar(todos);
  };

  const rendidos = filas.filter((f) => f.estado === 'rendido');

  return (
    <div style={{ ...CAJA, borderLeft: `3px solid ${rendidos.length ? C.orange : C.teal}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ ...TITULO, margin: 0 }}>Cadena automática</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setTodos((v) => !v)}
            style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', fontSize: 11.5, cursor: 'pointer' }}>
            {todos ? 'Solo pendientes' : 'Ver también los hechos'}
          </button>
          <button type="button" onClick={empujar}
            style={{ background: C.teal, color: C.heading, border: 'none', borderRadius: 8, padding: '6px 14px', fontWeight: 800, fontSize: 11.5, cursor: 'pointer' }}>
            Procesar ahora
          </button>
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
        El pago aprobado encola <strong style={{ color: C.heading }}>factura → recibo de caja → asiento de la
        comisión</strong>; la obra cerrada encola el <strong style={{ color: C.heading }}>giro al técnico</strong>.
        Corre sola cada 5 minutos, con reintentos.
        {rendidos.length > 0 && (
          <> <strong style={{ color: C.orange }}>Hay {rendidos.length} que se rindieron: eso es plata sin
          documento, hay que mirarlo.</strong></>
        )}
      </div>
      {msg && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 8 }}>{msg}</div>}
      {filas.length === 0 ? (
        <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>Nada pendiente.</div>
      ) : (
        <div style={{ marginTop: 10, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead>
              <tr>{['Trabajo', 'Referencia', 'Estado', 'Intentos', 'Último error'].map((t) => (
                <th key={t} style={{ textAlign: 'left', fontSize: 9.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, padding: '8px 6px', borderBottom: `1px solid ${C.border}` }}>{t}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filas.slice(0, 20).map((f) => {
                const col = f.estado === 'rendido' ? C.orange : (f.estado === 'hecho' ? C.teal : C.amber);
                return (
                  <tr key={f.clave}>
                    <td style={{ fontSize: 12, color: C.heading, fontWeight: 600, padding: '7px 6px' }}>{f.tipo}</td>
                    <td style={{ fontSize: 12, color: C.text, padding: '7px 6px' }}>{f.ref}</td>
                    <td style={{ padding: '7px 6px' }}>
                      <span style={{ fontSize: 9.5, fontWeight: 700, color: col, background: `${col}18`, border: `1px solid ${col}44`, borderRadius: 5, padding: '2px 8px' }}>{f.estado}</span>
                    </td>
                    <td style={{ fontSize: 12, color: C.text, padding: '7px 6px' }}>{f.intentos}</td>
                    <td style={{ fontSize: 11, color: C.muted, padding: '7px 6px', maxWidth: 320 }}>{f.ultimo_error || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * EL MOTOR DE PAGOS DE LA SUITE, dicho en pantalla.
 *
 * Un solo emisor para todo Alebas: cuando está encendido, el cobro, la factura DIAN, el recibo
 * de caja y el documento soporte salen por la cuenta Siigo del api de FluxAI — la misma empresa,
 * el mismo consecutivo. Las credenciales locales de Siigo pasan a ser respaldo.
 *
 * Sin este cartel, la tarjeta de abajo («Siigo sin configurar») se lee como una falla y manda a
 * cargar credenciales que no hacen falta — y peor, invita a crear una SEGUNDA numeración DIAN.
 */
function MotorDeLaSuite() {
  const [m, setM] = React.useState(null);

  React.useEffect(() => {
    let vivo = true;
    listarMediosPago()
      .then((r) => { if (vivo) setM(r?.motor || { disponible: false, motivo: 'El hub-api no informó el estado del motor.' }); })
      .catch((e) => { if (vivo) setM({ disponible: false, motivo: e.message }); });
    return () => { vivo = false; };
  }, []);

  if (!m) return null;
  const on = Boolean(m.disponible);
  return (
    <div style={{ ...CAJA, borderLeft: `3px solid ${on ? C.teal : C.orange}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: on ? C.teal : C.orange }} />
        <h3 style={{ ...TITULO, margin: 0 }}>
          Motor de pagos de Alebas · {on ? 'ENCENDIDO' : 'apagado'}
        </h3>
      </div>
      <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8, lineHeight: 1.55 }}>
        {m.motivo}
      </div>
      {on && (
        <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.55 }}>
          Con el motor encendido, las llaves de Siigo de abajo son <strong style={{ color: C.heading }}>respaldo</strong>:
          cargarlas acá crearía una <strong style={{ color: C.heading }}>segunda numeración DIAN</strong> de la
          misma empresa, y dos consecutivos no se reconcilian después.
        </div>
      )}
      {/*
        🔴 QUÉ PASA SI ESTE MOTOR SE CAE. Decir «respaldo» sin decir si ese respaldo RECAUDA es
        media verdad: medido el 2026-08-29, las llaves locales están en sandbox y no hay cuenta de
        transferencia cargada, así que la caída no recibe un peso. El checkout ya responde una
        negativa clara en vez de abrir un cobro que no cobra — pero eso hay que verlo ANTES de que
        se caiga, no descubrirlo el día que pase.
      */}
      {m.respaldo_local && (
        <div style={{
          fontSize: 11,
          color: C.muted,
          marginTop: 8,
          lineHeight: 1.55,
          borderTop: `1px solid ${C.border}`,
          paddingTop: 8,
        }}>
          <strong style={{ color: m.respaldo_local.recauda ? C.heading : C.orange }}>
            Si el motor se cae: {m.respaldo_local.recauda
              ? `cobra el respaldo local (${m.respaldo_local.via}${m.respaldo_local.medio ? ` · ${m.respaldo_local.medio}` : ''})`
              : 'NO se puede cobrar'}
          </strong>
          {' — '}{m.respaldo_local.motivo}
          {m.respaldo_local.prueba && (
            <div style={{ color: C.orange, marginTop: 4 }}>
              ⚠️ Ese respaldo son llaves de PRUEBAS: el checkout abriría y diría «aprobado» sin mover plata.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FacturacionSiigo() {
  const [est, setEst] = React.useState(null);
  const [cat, setCat] = React.useState(null);
  const [docs, setDocs] = React.useState([]);
  const [sel, setSel] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const [cargando, setCargando] = React.useState(true);

  const cargar = React.useCallback(async () => {
    setCargando(true);
    try {
      const r = await estadoSiigo();
      setEst(r || null);
    } catch (e) {
      setEst({ ok: false, motivo: e.message });
    }
    setCargando(false);
  }, []);

  React.useEffect(() => { cargar(); }, [cargar]);

  const verCatalogos = async () => {
    setMsg('Consultando el catálogo de la cuenta…');
    const r = await catalogosSiigo().catch((e) => ({ ok: false, error: e.message }));
    setCat(r);
    setMsg(r?.ok ? '' : (r?.error || 'No se pudo leer el catálogo.'));
  };

  const verDocumentos = async () => {
    setMsg('Consultando documentos…');
    const r = await listarDocumentosSiigo({ tipo: 'factura' }).catch((e) => ({ ok: false, error: e.message }));
    setDocs(r?.documentos || []);
    setMsg(r?.ok ? '' : (r?.motivo || r?.error || 'No se pudo listar.'));
  };

  const accion = async (fn, id, etiqueta) => {
    setMsg(`${etiqueta}…`);
    const r = await fn(id).catch((e) => ({ ok: false, error: e.message }));
    if (r?.ok) {
      setSel({ id, ...r });
      setMsg('');
    } else {
      setMsg(r?.error || 'Siigo respondió con un error.');
    }
  };

  const cfg = est?.catalogo || {};
  const h = est?.herramientas || {};

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {/*
        🔑 QUIÉN COBRA Y FACTURA, arriba de todo. Sin esto, ver «Siigo sin configurar» en esta
        pantalla manda a cargar credenciales que NO hacen falta: con el motor de la suite
        encendido, la factura, el recibo y el documento soporte salen por la cuenta Siigo de
        Alebas que vive en el api de FluxAI. Una capacidad que ninguna pantalla muestra es una
        capacidad que el dueño cree no tener.
      */}
      <MotorDeLaSuite />

      <div style={CAJA}>
        <h3 style={TITULO}>Conexión con Siigo</h3>
        {cargando ? (
          <div style={{ fontSize: 12, color: C.muted }}>Consultando…</div>
        ) : (
          <>
            {/*
              🔴 ESTE SEMÁFORO YA MINTIÓ: responde por el MOTOR cuando está encendido, así que
              decía «listo» con las credenciales locales vacías — y ese diagnóstico invita a
              cargarlas acá, o sea a crear una segunda numeración DIAN. Ahora dice de qué vía
              habla; sigue siendo un solo semáforo, pero ya no se puede leer al revés.
            */}
            <Semaforo
              ok={est?.credenciales}
              label={`Credenciales${est?.via === 'motor' ? ' (vía motor de la suite)' : (est?.via === 'local' ? ' (llaves locales del hub-api)' : '')}`}
              detalle={est?.credenciales ? `Partner-Id ${est.partner_id}` : 'Faltan SIIGO_USERNAME / SIIGO_ACCESS_KEY en el hub-api'}
            />
            <Semaforo ok={est?.auth} label="Autenticación" detalle={est?.auth ? 'Siigo aceptó las llaves' : 'Siigo no autenticó'} />
            <Semaforo ok={est?.configurada} label="Lista para emitir" detalle={est?.motivo} />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
              {/* El catálogo se resuelve solo: los SIIGO_*_ID del env son override, no requisito. */}
              El catálogo (documento, IVA, medio de pago, centro de costo) lo resuelve la propia cuenta.
              Los <code>SIIGO_*_ID</code> del entorno solo se usan para forzar uno distinto.
            </div>
            <button type="button" onClick={cargar}
              style={{ marginTop: 10, background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 8, padding: '6px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              Volver a probar
            </button>
          </>
        )}
      </div>

      <div style={CAJA}>
        <h3 style={TITULO}>Herramientas disponibles en la cuenta</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 2 }}>
          {HERRAMIENTAS.map(([k, label]) => <Semaforo key={k} ok={Boolean(h[k])} label={label} />)}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
          {/* Un DS que no existe no se inventa: el giro se registra igual y el hueco queda visible. */}
          Si el <strong style={{ color: C.heading }}>documento soporte</strong> está en ámbar, los giros al
          técnico se registran pero <strong style={{ color: C.heading }}>sin respaldo DIAN</strong>: ese costo
          no es deducible hasta que la cuenta tenga el tipo DS activo.
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
          Centro de costo resuelto: <strong style={{ color: C.heading }}>{cfg.cost_center_id || '—'}</strong>
          {' · '}Documento FV: <strong style={{ color: C.heading }}>{cfg.document_id || '—'}</strong>
          {' · '}Medio: <strong style={{ color: C.heading }}>{cfg.payment_id || '—'}</strong>
        </div>
      </div>

      <ColaAutomatica />

      <PedidoFiscal />

      <div style={CAJA}>
        <h3 style={TITULO}>Catálogo y documentos de la cuenta</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={verCatalogos}
            style={{ background: C.teal, color: C.heading, border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
            Ver catálogo real
          </button>
          <button type="button" onClick={verDocumentos}
            style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            Últimas facturas
          </button>
        </div>
        {msg && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 8 }}>{msg}</div>}

        {cat?.ok && (
          <div style={{ marginTop: 12, display: 'grid', gap: 8, fontSize: 11.5, color: C.text }}>
            {[['documento_venta', 'Facturas de venta'], ['nota_credito', 'Notas crédito'],
              ['documento_soporte', 'Documento soporte'], ['medios_pago', 'Medios de pago'],
              ['centros_costo', 'Centros de costo']].map(([k, label]) => (
                <div key={k}>
                  <strong style={{ color: C.heading }}>{label}:</strong>{' '}
                  {(cat[k] || []).length
                    ? (cat[k] || []).map((f) => `${f.nombre} (${f.id})${f.activo ? '' : ' · inactivo'}`).join(' · ')
                    : <span style={{ color: C.muted }}>la cuenta no tiene ninguno activo</span>}
                </div>
            ))}
          </div>
        )}

        {docs.length > 0 && (
          <div style={{ marginTop: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr>{['Número', 'Fecha', 'Total', 'DIAN', ''].map((t) => (
                  <th key={t} style={{ textAlign: 'left', fontSize: 9.5, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, padding: '8px 6px', borderBottom: `1px solid ${C.border}` }}>{t}</th>
                ))}</tr>
              </thead>
              <tbody>
                {docs.slice(0, 15).map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontSize: 12, color: C.heading, fontWeight: 600, padding: '7px 6px' }}>{d.numero || d.id}</td>
                    <td style={{ fontSize: 12, color: C.text, padding: '7px 6px' }}>{d.fecha || '—'}</td>
                    <td style={{ fontSize: 12, color: C.text, padding: '7px 6px' }}>
                      {d.total != null ? `$${Math.round(d.total).toLocaleString('es-CO')}` : '—'}
                    </td>
                    <td style={{ fontSize: 11, color: d.cufe ? C.teal : C.amber, padding: '7px 6px' }}>
                      {d.cufe ? 'CUFE ✓' : (d.estado_dian || 'sin estampar')}
                    </td>
                    <td style={{ padding: '7px 6px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => accion(consultarDocumentoSiigo, d.id, 'Consultando')}
                        style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>Ver</button>
                      <button type="button" onClick={() => accion(pdfDocumentoSiigo, d.id, 'Bajando PDF')}
                        style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>PDF</button>
                      <button type="button" onClick={() => accion((id) => reenviarDocumentoSiigo(id), d.id, 'Reenviando')}
                        style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>Reenviar</button>
                      {!d.cufe && (
                        <button type="button" onClick={() => accion((id) => estamparDocumentoSiigo(id), d.id, 'Estampando')}
                          style={{ background: 'transparent', color: C.teal, border: `1px solid ${C.teal}`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>Estampar</button>
                      )}
                      <button type="button" onClick={() => accion(erroresDianSiigo, d.id, 'Consultando el rechazo')}
                        style={{ background: 'transparent', color: C.orange, border: `1px solid ${C.orange}55`, borderRadius: 6, padding: '3px 9px', fontSize: 11, cursor: 'pointer' }}>¿Por qué falló?</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {sel && (
          <pre style={{ marginTop: 12, background: `${C.muted}12`, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 11, color: C.text, overflowX: 'auto', maxHeight: 260 }}>
            {JSON.stringify({ ...sel, base64: sel.base64 ? `${sel.base64.slice(0, 40)}… (${sel.base64.length} bytes)` : undefined, crudo: undefined }, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
